import { excelDateToJSDate } from '@listening/utils/ts-utils';
import { formatDate } from '@/server/lib/utils';
import {
  DocumentSummarySection,
  DocumentDetailsSection,
  DocumentKeyPointsSection,
  documentKeyPoints,
} from '@/components/shared-ui/document-insights';
import { clientEnv } from '@/env';
import { DocumentInsightsSidebar } from './document-insights-sidebar';
import {
  primaryButtonSurface,
  secondaryButtonSurface,
} from '@/components/web-fe/design-v2/surfaces';
import { effectQueryErrorMessage } from '@listening/frontend/query/query-error';
import { getNotePlaybackTarget } from '@listening/frontend/audio-chunk-player/note-playback';
import {
  activeDocPackOutlineNavigationItem,
  docPackOutlineNavigationItems,
} from '@listening/frontend/doc-pack/navigation';
import { docPackCatalogArgs } from '@listening/frontend/doc-pack/source';
import { useEq } from '@listening/frontend/query/runtime-context';
import { useDocPackUsable } from '@/lib/doc-reader/pack/doc-pack-source';
import type { NoteRow as Note } from '@listening/frontend/query/notes';
import { cn } from '@listening/utils/react-helpers';
import AddIcon from '@listening/assets/icons/add.svg?react';
import DeleteIcon from '@listening/assets/icons/delete.svg?react';
import DownloadIcon from '@listening/assets/icons/download.svg?react';
import NextIcon from '@listening/assets/icons/next.svg?react';
import PencilIcon from '@listening/assets/icons/pencil.svg?react';
import ShareIcon from '@listening/assets/icons/share2.svg?react';
import VolumeIcon from '@listening/assets/icons/volume.svg?react';

import { Button } from '@listening/frontend/components/radix/button';
import {
  Dialog,
  DialogTrigger,
} from '@listening/frontend/components/radix/dialog';
import { DialogCloseProvider } from '@listening/frontend/components/custom/dialog-dropdown';
import { ScrollArea } from '@listening/frontend/components/radix/scroll-area';
import { TooltipButton } from '@listening/frontend/components/custom/tooltip-button';
import { Play, Xmark } from 'iconoir-react';
import type React from 'react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import * as m from '@/paraglide/messages';
import { ErrorComponent } from '@/components/shared-ui/error-component';
import { LoadingComponent } from '@/components/shared-ui/loading-component';
import { Spinner } from '@/components/shared-ui/spinner';
import { EditNoteDialog } from '@/components/web-fe/dialogs/edit-note-dialog';
import { ExportNotesDialog } from '@/components/web-fe/dialogs/export-notes-dialog';
import ShareNotesDialog from '@/components/web-fe/dialogs/share-notes-dialog';
import EmptyNotes from '@/components/web-fe/notes/empty-notes';
import {
  useAudioChunkPlayer,
  useAudioChunkSelector,
  useDocumentSummary,
} from '@/lib/audio-chunk-player';
import { useAnalytics } from '@listening/frontend/analytics/client';
import { audioService } from '@/services/audio-service';
import {
  useAddAudioNote,
  useDeleteNoteMutation,
} from '@/services/notes-service';
import { useFilteredNotesQuery } from '@listening/frontend/query/notes-collection';
import {
  type SidebarState,
  useReadAlongContext,
  useReadAlongPageType,
  useReadAlongSidebarStore,
} from './read-along-store';

const contentMap: Record<SidebarState, React.ReactNode> = {
  notes: <NotesSideBar />,
  information: <ReadAlongMetadataSidebar />,
  sections: <ChaptersSideBar />,
  closed: null,
};

export function ReadAlongSideBar({ className }: { className?: string }) {
  const { setSidebarState, sidebarState } = useReadAlongSidebarStore();
  const { isWebAppDoc } = useReadAlongPageType();
  const showInsights = isWebAppDoc && clientEnv.documentInsightsEnabled;
  const isClosed =
    sidebarState === 'closed' ||
    (sidebarState === 'information' && showInsights);
  const titleMap: Record<SidebarState, string> = {
    notes: m.common_notes(),
    information: m.read_along_information(),
    sections: m.playbar_sections(),
    closed: '',
  };

  return (
    <>
      {showInsights && <DocumentInsightsSidebar className={className} />}
      <section
        className={cn(
          'absolute inset-y-0 right-0 z-5 flex w-full flex-col overflow-hidden bg-[var(--v2-page-bg)] sm:relative sm:w-[22.75rem] sm:shrink-0',
          isClosed
            ? 'hidden'
            : 'border-l border-l-[var(--v2-sidebar-border)] pt-6',
          className,
        )}
      >
        {!isClosed && (
          <div className='flex items-center justify-between px-8 pb-2'>
            <h3 className='text-xl leading-[1.875rem] font-[700]'>
              {titleMap[sidebarState]}
            </h3>

            <TooltipButton
              className='p-1'
              onClick={() => {
                setSidebarState('closed');
              }}
              size='sm'
              toolTipContent={m.common_close()}
              variant='ghost'
            >
              <Xmark className='size-4' />
            </TooltipButton>
          </div>
        )}
        {!isClosed && contentMap[sidebarState]}
      </section>
    </>
  );
}

function ChaptersSideBar() {
  const { documentSummary } = useDocumentSummary();
  const audioChunkPlayer = useAudioChunkPlayer();
  const chapterId = useAudioChunkSelector((s) => s.chapterId);
  const playbackMode = useAudioChunkSelector((s) => s.playbackMode);
  const docPackChunk = useAudioChunkSelector((s) => s.docPackChunk);
  const { capture } = useAnalytics();
  const eq = useEq();
  const docPackUsable = useDocPackUsable(documentSummary?.doc_pack);
  const docPackInfo =
    documentSummary && docPackUsable ? documentSummary.doc_pack : undefined;
  const docPackCatalogQuery = useQuery({
    ...eq.queryOptions(
      docPackCatalogArgs(documentSummary?.id ?? '', docPackInfo?.url ?? ''),
    ),
    enabled: !!documentSummary && !!docPackInfo,
  });
  if (!documentSummary) return <p>{m.read_along_no_audio_playing()}</p>;

  const docPackOutlineItems = docPackCatalogQuery.data
    ? docPackOutlineNavigationItems(docPackCatalogQuery.data.catalog)
    : [];
  const firstDocPackRange = docPackChunk?.ranges[0];
  const activeDocPackOutline = activeDocPackOutlineNavigationItem(
    docPackOutlineItems,
    firstDocPackRange
      ? { page: firstDocPackRange[0], charStart: firstDocPackRange[1] }
      : undefined,
  );
  const docPackOutlineListItems = docPackOutlineItems.map((item) => {
    const active =
      playbackMode === 'doc-pack' && activeDocPackOutline?.id === item.id;
    return (
      <Button
        className='flex h-auto w-full justify-between gap-2 p-4 text-left'
        key={item.id}
        onClick={() => {
          audioChunkPlayer.skipToDocPackRange({
            page: item.page,
            charStart: item.charStart,
          });
          capture('skip_to_chapter', {
            chapter_name: item.title,
          });
        }}
        style={{ paddingLeft: `${1 + item.depth * 0.75}rem` }}
        variant='outline'
      >
        <p className='font-bold text-wrap'>{item.title}</p>
        <div className='flex items-center gap-2'>
          {active ? (
            <VolumeIcon className='ml-auto text-accent-foreground' />
          ) : (
            <NextIcon className='ml-auto text-secondary-foreground' />
          )}
        </div>
      </Button>
    );
  });
  const chapterListItems = documentSummary.chapters.map((chapter) => (
    <Button
      className='flex h-auto w-full justify-between gap-2 p-4 text-left'
      key={chapter.id}
      onClick={() => {
        audioChunkPlayer.skipToChapterStart(chapter.id);
        capture('skip_to_chapter', {
          chapter_name: chapter.title,
        });
      }}
      variant='outline'
    >
      <p className='font-bold text-wrap'>{chapter.title}</p>
      <div className='flex items-center gap-2'>
        <p className='text-right text-azure-210' />
        {chapterId === chapter.id ? (
          <VolumeIcon className='ml-auto text-accent-foreground' />
        ) : (
          <NextIcon className='ml-auto text-secondary-foreground' />
        )}
      </div>
    </Button>
  ));
  const sectionItems =
    docPackOutlineListItems.length > 0
      ? docPackOutlineListItems
      : chapterListItems;
  return (
    <ScrollArea className='min-h-0 flex-1'>
      <div className='space-y-4 px-8 py-2 pb-32'>{sectionItems}</div>
    </ScrollArea>
  );
}

function ReadAlongMetadataSidebar() {
  const metadata = useReadAlongContext((s) => s.metadata);
  const { documentSummary } = useDocumentSummary();
  const summary = metadata?.abstract || documentSummary?.summary;
  const points = documentKeyPoints(documentSummary?.key_points);
  const authors = metadata?.authors || documentSummary?.publishing_author;
  const category = metadata?.category || documentSummary?.category;

  return (
    <ScrollArea className='min-h-0 flex-1'>
      <div className='flex flex-col gap-7 px-8 py-4 pb-32'>
        <h2 className='text-lg font-semibold text-foreground'>
          {metadata?.title || documentSummary?.title}
        </h2>
        {summary && <DocumentSummarySection summary={summary} />}
        {points.length > 0 && <DocumentKeyPointsSection points={points} />}
        {!summary &&
          points.length === 0 &&
          !authors &&
          !category &&
          !metadata?.source &&
          !metadata?.publication_date && (
            <p className='text-sm text-muted-foreground'>
              {m.read_along_no_metadata()}
            </p>
          )}
        {(authors || category || metadata?.source) && (
          <DocumentDetailsSection
            authors={authors}
            category={category}
            source={metadata?.source}
          />
        )}
        {metadata?.publication_date && (
          <div className='text-sm'>
            <span className='font-medium'>
              {m.read_along_publication_date()}:{' '}
            </span>
            {!Number.isNaN(Number.parseInt(metadata.publication_date, 10))
              ? formatDate(
                  excelDateToJSDate(
                    Number.parseInt(metadata.publication_date, 10),
                  ),
                )
              : m.read_along_not_available()}
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

function NotesSideBar() {
  const audioItem = audioService.store.useCurrentAudio().query.data;
  const listNotesQuery = useFilteredNotesQuery({
    audio_conversion_id: audioItem?.id,
  });
  const { addAudioNote, upsertNoteMutation } = useAddAudioNote();

  if (!audioItem) return <p>{m.read_along_start_playing_for_notes()}</p>;
  if (listNotesQuery.isPending) return <LoadingComponent />;
  if (listNotesQuery.isError)
    return (
      <ErrorComponent
        header={
          effectQueryErrorMessage(listNotesQuery.error) ??
          m.read_along_failed_to_load_notes()
        }
      />
    );

  const notes = listNotesQuery.data;

  return (
    <>
      <div className='mt-1 mb-6 flex flex-row items-center gap-2 px-8'>
        <Button
          className={cn(
            'flex h-9 items-center gap-1.5 px-3.5 text-[13px] font-semibold',
            primaryButtonSurface,
          )}
          onClick={addAudioNote}
        >
          {upsertNoteMutation.isPending ? (
            <Spinner />
          ) : (
            <AddIcon className='h-3.5 w-3.5' />
          )}
          {m.read_along_add_new_note()}
        </Button>
        <Dialog>
          <DialogTrigger asChild>
            <Button
              className={cn(
                'flex h-9 items-center gap-1.5 px-3.5 text-[13px] font-semibold',
                secondaryButtonSurface,
              )}
            >
              <DownloadIcon className='h-3.5 w-3.5' />
              {m.read_along_export_all_notes()}
            </Button>
          </DialogTrigger>
          <ExportNotesDialog notes={notes} />
        </Dialog>
      </div>
      {notes.length === 0 && <EmptyNotes />}
      <ScrollArea className='min-h-0 flex-1'>
        <div className='space-y-4 py-2'>
          {notes.map((note) => (
            <NotesSidebarListItem
              key={note.id}
              note={note}
              documentTitle={audioItem.title ?? undefined}
            />
          ))}
        </div>
        {/* Notes auto-load via the collection's cursor-follow queryFn — the
            "Load more" button became unreachable when notes migrated. */}
      </ScrollArea>
    </>
  );
}

const ICON_BUTTON =
  'flex size-8 items-center justify-center rounded-[var(--v2-button-radius)] p-0 text-[var(--v2-text-muted)] transition-colors hover:bg-black/[0.05] hover:text-[var(--v2-text-primary)] disabled:pointer-events-none disabled:opacity-40 dark:hover:bg-white/10';

function NotesSidebarListItem({
  note,
  documentTitle,
}: {
  note: Note;
  documentTitle?: string;
}) {
  const deleteNoteMutation = useDeleteNoteMutation();
  const audioChunkPlayer = useAudioChunkPlayer();
  const playbackTarget = getNotePlaybackTarget(note);
  // Captured notes default their title to the document's, which the panel
  // header already shows — but a title edited in `EditNoteDialog` is the
  // user's own and has to stay visible.
  const hasCustomTitle = note.title !== '' && note.title !== documentTitle;

  return (
    <div className='mx-8 flex flex-col gap-2 overflow-clip rounded-[var(--v2-card-radius)] border-[length:var(--v2-border-width)] border-[var(--v2-card-border)] bg-[var(--v2-page-bg)]'>
      <div className='flex flex-col gap-1 p-[var(--v2-gap-4)] pb-0'>
        {hasCustomTitle && (
          <p
            className='text-[13px] text-[var(--v2-text-primary)]'
            style={{ fontWeight: 'var(--v2-heading-weight)' }}
          >
            {note.title}
          </p>
        )}
        <p className='text-[14px] leading-relaxed whitespace-pre-wrap text-[var(--v2-text-primary)]'>
          {note.body}
        </p>
      </div>
      <div className='flex items-center gap-0.5 px-[var(--v2-gap-2)] pb-[var(--v2-gap-2)]'>
        <TooltipButton
          className={ICON_BUTTON}
          disabled={!playbackTarget}
          onClick={() => {
            if (!playbackTarget) return;
            if (playbackTarget.type === 'doc-pack') {
              audioChunkPlayer.skipToDocPackRange(
                playbackTarget.anchor,
                playbackTarget.loadParams,
              );
              return;
            }
            audioChunkPlayer.skipToChunk(
              playbackTarget.ttsId,
              playbackTarget.loadParams,
            );
          }}
          toolTipContent={m.common_play()}
          variant='ghost'
        >
          <Play className='size-3.5 shrink-0 fill-current' strokeWidth={0} />
        </TooltipButton>
        <DialogCloseProvider>
          <DialogTrigger asChild>
            <TooltipButton
              className={ICON_BUTTON}
              toolTipContent={m.common_edit()}
              variant='ghost'
            >
              <PencilIcon className='size-4' />
            </TooltipButton>
          </DialogTrigger>
          <EditNoteDialog noteItem={note} />
        </DialogCloseProvider>
        <DialogCloseProvider>
          <DialogTrigger asChild>
            <TooltipButton
              className={ICON_BUTTON}
              toolTipContent={m.common_share()}
              variant='ghost'
            >
              <ShareIcon className='size-4' />
            </TooltipButton>
          </DialogTrigger>
          <ShareNotesDialog note={note} />
        </DialogCloseProvider>
        <DialogCloseProvider>
          <DialogTrigger asChild>
            <TooltipButton
              className={ICON_BUTTON}
              toolTipContent={m.dialog_export()}
              variant='ghost'
            >
              <DownloadIcon className='size-4' />
            </TooltipButton>
          </DialogTrigger>
          <ExportNotesDialog notes={[note]} />
        </DialogCloseProvider>

        <TooltipButton
          className={cn(
            'ml-auto',
            ICON_BUTTON,
            'hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400',
          )}
          onClick={() => {
            deleteNoteMutation.mutate(
              { id: note.id },
              {
                onError: (e) => {
                  toast.error(
                    effectQueryErrorMessage(e) ??
                      m.read_along_error_deleting_note(),
                  );
                },
              },
            );
          }}
          toolTipContent={m.common_delete()}
          variant='ghost'
        >
          {deleteNoteMutation.isPending ? (
            <Spinner />
          ) : (
            <DeleteIcon className='h-4 w-4 text-red-500 transition-colors duration-200 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300' />
          )}
        </TooltipButton>
      </div>
    </div>
  );
}
